<!--
  =====================================================================
  APP ROUTER - DYNAMIC PAGE LOADER
  =====================================================================
  
  Root component that dynamically routes to different page views based on
  the current URL pathname. Handles both direct routes (e.g., /cassa/news)
  and dynamic routes with parameters (e.g., /cassa/news/news-slug).
  
  URL ROUTING PATTERNS:
  - /cassa/                         → HomePage
  - /cassa/chi-siamo                → ChiSiamoPage
  - /cassa/prodotti                 → ProductsHome
  - /cassa/prodotti/:macroArea/:category        → ProductsCategory
  - /cassa/prodotti/:macroArea/:category/:product → ProductsDetail
  - /cassa/opere-arte               → OperePage
  - /cassa/news                     → NewsPage
  - /cassa/news/:slug               → NewsSinglePage
  - /cassa/filiali                  → FilialiPage
  - /cassa/filiali/:slug            → FilialiSubPage
  - /cassa/informative-al-pubblico  → InformativeAlPubblicoPage
  - /cassa/informative-al-pubblico/:slug → InformativeAlPubblicoSinglePage

  NOTES:
  - Supports legacy URLs without /cassa prefix (redirects via handler)
  - Uses 'popstate' event listener to handle browser back/forward buttons
  - Routing logic is organized by specificity (detailed routes first)

  =====================================================================
-->

<template>
  <div>
    <component :is="componenteCorrente" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

// ───────────────────────────────────────────────────────────────────
// PAGE VIEW IMPORTS
// ───────────────────────────────────────────────────────────────────
import HomePage from './views/HomePage.vue'
import ChiSiamoPage from './views/ChiSiamoPage.vue'
import ProductsHome from './views/ProductsHome.vue'
import ProductsCategory from './views/ProductsCategory.vue'
import ProductsDetail from './views/ProductsDetail.vue'
import OperePage from './views/OperePage.vue'
import NewsPage from './views/NewsPage.vue'
import NewsSinglePage from './views/NewsSinglePage.vue'
import FilialiPage from './views/FilialiPage.vue'
import FilialiSubPage from './views/FilialiSubPage.vue'
import InformativePage from "@/views/InformativeAlPubblicoPage.vue"
import InformativaSinglePage from "@/views/InformativeAlPubblicoSinglePage.vue"
import NotFoundPage from './views/NotFoundPage.vue'

// Import global state for 404 handling
import { state } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE
// ───────────────────────────────────────────────────────────────────

/** Current pathname tracked for routing decisions */
const percorso = ref(window.location.pathname)

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize router event listeners
 * - Listens for popstate events (browser back/forward buttons)
 * - Updates current route when user navigates via browser buttons
 * - Shows loading screen on route change
 * - Redirects URLs without /cassa/ prefix to home
 */
watch(() => state.loading, (isLoading) => {
  const loadingScreen = document.getElementById('loading-screen')
  if (!loadingScreen) return
  if (isLoading) {
    loadingScreen.classList.remove('hidden')
  } else {
    loadingScreen.classList.add('hidden')
  }
})

onMounted(() => {
  const currentPath = window.location.pathname
  if (!currentPath.startsWith('/cassa/') && currentPath !== '/cassa') {
    console.info(`Redirect: ${currentPath} → /cassa/`)
    window.location.href = '/cassa/'
    return
  }

  window.addEventListener('popstate', () => {
    percorso.value = window.location.pathname
  })
})

// ───────────────────────────────────────────────────────────────────
// STATIC ROUTE MAPPING
// ───────────────────────────────────────────────────────────────────

/**
 * Maps simple routes (without parameters) to their corresponding components
 * These are fallback routes when no dynamic path matches
 */
const mappa = {
  '/cassa/': HomePage,
  '/cassa/chi-siamo': ChiSiamoPage,
  '/cassa/prodotti': ProductsHome,
  '/cassa/opere-arte': OperePage,
  '/cassa/news': NewsPage,
  '/cassa/filiali': FilialiPage,
  '/cassa/informative-al-pubblico': InformativePage,
}

// ───────────────────────────────────────────────────────────────────
// DYNAMIC ROUTE RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Resolves the current page component based on the URL pathname
 *
 * Routing algorithm:
 * 1. Parse URL path into segments, removing empty parts
 * 2. Skip 'cassa' prefix if present (Nginx serves app under /cassa/)
 * 3. Check for detailed routes first (longer path segments)
 * 4. Then check for main section routes
 * 5. Finally fall back to static mapping
 *
 * @returns {VueComponent} The component to render for current route
 */
const componenteCorrente = computed(() => {
  // ✅ PRIORITY 1: Check for centralized 404 handling
  if (state.notFound) {
    return NotFoundPage
  }

  const pathSegments = percorso.value.split('/').filter(Boolean)

  // Normalize path: remove 'cassa' prefix if present
  const segments = pathSegments[0] === 'cassa' ? pathSegments.slice(1) : pathSegments

  // ─── PRODOTTI: Detailed Routes (3-4 segments) ───────────────────
  // Product Detail: /cassa/prodotti/:macroArea/:category/:product
  if (segments[0] === 'prodotti' && segments.length === 4) {
    return ProductsDetail
  }

  // Product Category: /cassa/prodotti/:macroArea/:category
  if (segments[0] === 'prodotti' && segments.length === 3) {
    return ProductsCategory
  }

  // Product Home: Various formats
  if (percorso.value === '/prodotti' || percorso.value === '/prodotti/' ||
      percorso.value === '/cassa/prodotti' || percorso.value === '/cassa/prodotti/')
    return ProductsHome

  // ─── FILIALI: Detailed Routes ──────────────────────────────────
  // Filiali SubPage: /cassa/filiali/:slug
  if ((percorso.value.startsWith('/filiali/') || percorso.value.startsWith('/cassa/filiali/')) &&
      percorso.value !== '/filiali' && percorso.value !== '/cassa/filiali')
    return FilialiSubPage

  // ─── NEWS: Detailed Routes ────────────────────────────────────
  // News Single: /cassa/news/:slug
  if ((percorso.value.startsWith('/news/') || percorso.value.startsWith('/cassa/news/')) &&
      percorso.value !== '/news' && percorso.value !== '/cassa/news')
    return NewsSinglePage

  // ─── INFORMATIVE: Detailed Routes ─────────────────────────────
  // Informativa Single: /cassa/informative-al-pubblico/:slug
  if ((percorso.value.startsWith('/informative-al-pubblico/') || percorso.value.startsWith('/cassa/informative-al-pubblico/')) &&
      percorso.value !== '/informative-al-pubblico' && percorso.value !== '/cassa/informative-al-pubblico')
    return InformativaSinglePage

  // ─── FALLBACK: Static Route Mapping or 404 ──────────────────
  // Check if path is in known static routes
  if (mappa[percorso.value]) {
    return mappa[percorso.value]
  }

  // ✅ SOLID LOGIC: If not in mappa and not matching any dynamic patterns → 404
  // This ensures ANY unrecognized URL shows 404, not home
  return NotFoundPage
})
</script>

